import React, { useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import PhoneField from "../../../../components/phoneField";
import useCatalogosAlumno, { limpiarIdentificacion } from "../../../../components/enrollment/catalogosAlumno";
import { RequestStudentData } from "../../../../models/EnrollmentHistoryDTO";
import { nombrePropioTexto, primeraMayuscula, textoPropio } from "../../../../utils/textoPropio";
import { telefonoIncompleto } from "../../../../utils/mismoTelefono";
import { correoInvalido } from "../../../../utils/correoInvalido";
import S from "../../managment/enrollmentRequests/strings";
import { problemaIdentificacion, todayIso } from "../../managment/enrollmentRequests/helpers";

/** Select con una sola flecha: el select va invisible encima del texto, porque el iPhone le pinta su propia flecha aunque lleve appearance: none. */
const PsSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ children, ...props }) => {
	const ref = useRef<HTMLSelectElement>(null);
	const [texto, setTexto] = useState("");
	useLayoutEffect(() => { setTexto(ref.current?.selectedOptions[0]?.text ?? ""); });
	return (
		<span className="psSelectCaja">
			<span className="psSelect" aria-hidden="true">{texto}</span>
			<select ref={ref} {...props}>{children}</select>
		</span>
	);
};

export interface DatosClienteProps {
	clave: number;
	countryId: number;
	datos: RequestStudentData;
	onChange: (datos: RequestStudentData) => void;
}

/** Datos del alumno vacíos, con el país del plantel y su identificación de siempre (como el formulario de solicitud). */
export const datosIniciales = (countryId: number, tipoId: any, nombre: string): RequestStudentData => {
	const w = nombre.trim().split(/\s+/).filter(Boolean);
	const n = w.length >= 4 ? 2 : 1;
	return {
		firstName: nombrePropioTexto(w.slice(0, n).join(" ")), lastName: nombrePropioTexto(w.slice(n).join(" "), true), countryId, nationalityId: countryId,
		identificationTypeId: tipoId || "", contacts: [{ name: "", relation: "", contactMedia: "" }], links: { facebook: "", instagram: "" }
	};
};

/** Lo que de verdad se escribió: sin vacíos, para no mandar ruido al servidor. */
export const datosParaEnviar = (d: RequestStudentData): RequestStudentData | null => {
	const out: any = {};
	Object.entries(d).forEach(([k, v]) => {
		if (k === "contacts") {
			const reales = (v as any[] || []).filter((c) => String(c?.name || "").trim() !== "" || String(c?.contactMedia || "").trim() !== "");
			if (reales.length) out.contacts = reales;
		} else if (k === "links") {
			const l = Object.fromEntries(Object.entries(v || {}).filter(([, x]) => String(x || "").trim() !== ""));
			if (Object.keys(l).length) out.links = l;
		} else if (v !== null && v !== undefined && String(v).trim() !== "") {
			out[k] = v;
		}
	});
	return Object.keys(out).length ? out : null;
};

const Etiqueta: React.FC<{ htmlFor?: string; children: React.ReactNode }> = ({ htmlFor, children }) => (
	<label className="psEtiqueta" htmlFor={htmlFor}>{children}</label>
);

/**
 * "Llenar más datos": los mismos campos que piden los pasos Alumno, Contacto, Domicilio, Estudios y Emergencia
 * del formulario de solicitud, con sus mismos catálogos y correcciones. Nada es obligatorio aquí: lo que
 * mande la asesora llena la solicitud y la supervisora solo revisa (Esteban, 2026-10-03).
 */
const DatosCliente: React.FC<DatosClienteProps> = ({ clave, countryId, datos, onChange }) => {
	const { states, cities, academicDegrees, identificationType, countries, campusCountries, genders, identificationTypes } = useCatalogosAlumno(datos);
	const f = S.fields;
	const id = (campo: string) => `ps${campo}${clave}`;
	const poner = (campo: keyof RequestStudentData, valor: any) => onChange({ ...datos, [campo]: valor });
	const contactos = datos.contacts && datos.contacts.length ? datos.contacts : [{ name: "", relation: "", contactMedia: "" }];
	const ponerContacto = (i: number, campo: "name" | "relation" | "contactMedia", valor: string) =>
		poner("contacts", contactos.map((c, j) => (j === i ? { ...c, [campo]: valor } : c)));
	const malId = problemaIdentificacion(datos.identificationCode, identificationType);
	const malCorreo = correoInvalido(datos.email);
	const malTel2 = telefonoIncompleto(datos.phone2);

	return (
		<div className="psDatos">
			<div className="psDatosSub">Alumno</div>
			<div className="psDos">
				<div>
					<Etiqueta htmlFor={id("Nom")}>{f.firstName}</Etiqueta>
					<input id={id("Nom")} className="psInput" value={datos.firstName || ""} onChange={(e) => poner("firstName", e.target.value)}
						onBlur={(e) => poner("firstName", nombrePropioTexto(e.target.value))} />
				</div>
				<div>
					<Etiqueta htmlFor={id("Ape")}>{f.lastName}</Etiqueta>
					<input id={id("Ape")} className="psInput" value={datos.lastName || ""} onChange={(e) => poner("lastName", e.target.value)}
						onBlur={(e) => poner("lastName", nombrePropioTexto(e.target.value, true))} />
				</div>
				<div>
					<Etiqueta htmlFor={id("Nac")}>{f.birthday}</Etiqueta>
					<input id={id("Nac")} className="psInput" type="date" max={todayIso()} value={datos.birthday || ""} onChange={(e) => poner("birthday", e.target.value)} />
				</div>
				<div>
					<Etiqueta htmlFor={id("Gen")}>{f.genderId}</Etiqueta>
					<PsSelect id={id("Gen")} value={datos.genderId ?? ""} onChange={(e) => poner("genderId", e.target.value)}>
						<option value="">{S.select}</option>
						{genders.map((g: any) => <option key={"g_" + g.id} value={g.id}>{g.name}</option>)}
					</PsSelect>
				</div>
				<div>
					<Etiqueta htmlFor={id("TipoId")}>{f.identificationTypeId}</Etiqueta>
					<PsSelect id={id("TipoId")} value={datos.identificationTypeId || ""} onChange={(e) => poner("identificationTypeId", e.target.value)}>
						<option value="">{S.select}</option>
						{identificationTypes.map((t: any) => <option key={"it_" + t.id} value={t.id}>{t.name}</option>)}
					</PsSelect>
				</div>
				<div>
					<Etiqueta htmlFor={id("Id")}>{f.identificationCode}</Etiqueta>
					<input id={id("Id")} className="psInput" maxLength={identificationType?.max_length || 50} value={datos.identificationCode || ""}
						inputMode={identificationType?.bool_only_digits && Number(identificationType?.country_id) === 2 ? "numeric" : undefined}
						onChange={(e) => poner("identificationCode", limpiarIdentificacion(e.target.value, identificationType))} />
					{malId ? <div className="psFalta">{malId}</div> : null}
				</div>
			</div>

			<div className="psDatosSub">Contacto</div>
			<div className="psDos">
				<div>
					<Etiqueta htmlFor={id("Correo")}>{f.email}</Etiqueta>
					<input id={id("Correo")} className="psInput" type="email" inputMode="email" value={datos.email || ""} onChange={(e) => poner("email", e.target.value)}
						onBlur={(e) => poner("email", e.target.value.trim().toLowerCase())} />
					{malCorreo ? <div className="psFalta">{malCorreo}</div> : null}
				</div>
				<div>
					<Etiqueta htmlFor={id("Tel2")}>{f.phone2}</Etiqueta>
					<PhoneField id={id("Tel2")} name={id("Tel2")} value={datos.phone2 || ""} campusCountryId={countryId} onChange={(v: string) => poner("phone2", v)} />
					{malTel2 ? <div className="psFalta">{malTel2}</div> : null}
				</div>
				<div>
					<Etiqueta htmlFor={id("Fb")}>{f.facebook}</Etiqueta>
					<input id={id("Fb")} className="psInput" value={datos.links?.facebook || ""} onChange={(e) => poner("links", { ...(datos.links || {}), facebook: e.target.value })} />
				</div>
				<div>
					<Etiqueta htmlFor={id("Ig")}>{f.instagram}</Etiqueta>
					<input id={id("Ig")} className="psInput" value={datos.links?.instagram || ""} onChange={(e) => poner("links", { ...(datos.links || {}), instagram: e.target.value })} />
				</div>
			</div>

			<div className="psDatosSub">Domicilio</div>
			<div className="psDos">
				<div>
					<Etiqueta htmlFor={id("Nacion")}>{f.nationalityId}</Etiqueta>
					<PsSelect id={id("Nacion")} value={datos.nationalityId || ""} onChange={(e) => poner("nationalityId", e.target.value)}>
						<option value="">{S.select}</option>
						{countries.map((c: any) => <option key={"n_" + c.id} value={c.id}>{c.name}</option>)}
					</PsSelect>
				</div>
				<div>
					<Etiqueta htmlFor={id("Pais")}>{f.countryId}</Etiqueta>
					<PsSelect id={id("Pais")} value={datos.countryId || ""} onChange={(e) => onChange({ ...datos, countryId: e.target.value, stateId: "", cityId: "", stateName: "", cityName: "" })}>
						<option value="">{S.select}</option>
						{campusCountries.map((c: any) => <option key={"c_" + c.id} value={c.id}>{c.name}</option>)}
					</PsSelect>
				</div>
				<div>
					<Etiqueta htmlFor={id("Edo")}>{f.stateId}</Etiqueta>
					{states.length > 0 ? (
						<PsSelect id={id("Edo")} value={datos.stateId || ""} onChange={(e) => onChange({ ...datos, stateId: e.target.value, cityId: "", cityName: "",
							stateName: states.find((st: any) => String(st.id) === e.target.value)?.name || "" })}>
							<option value="">{S.select}</option>
							{states.map((st: any) => <option key={"s_" + st.id} value={st.id}>{st.name}</option>)}
						</PsSelect>
					) : <input id={id("Edo")} className="psInput" value={datos.stateName || ""} onChange={(e) => poner("stateName", e.target.value)} onBlur={(e) => poner("stateName", textoPropio(e.target.value))} />}
				</div>
				<div>
					<Etiqueta htmlFor={id("Cd")}>{f.cityId}</Etiqueta>
					{cities.length > 0 ? (
						<PsSelect id={id("Cd")} value={datos.cityId || ""} onChange={(e) => { const ci = cities.find((x: any) => String(x.id) === e.target.value); onChange({ ...datos, cityId: e.target.value, cityName: ci ? (ci.label || ci.name) : "" }); }}>
							<option value="">{S.select}</option>
							{cities.map((c: any) => <option key={"ci_" + c.id} value={c.id}>{c.label || c.name}</option>)}
						</PsSelect>
					) : <input id={id("Cd")} className="psInput" value={datos.cityName || ""} onChange={(e) => poner("cityName", e.target.value)} onBlur={(e) => poner("cityName", textoPropio(e.target.value))} />}
				</div>
			</div>
			<Etiqueta htmlFor={id("Dir")}>{f.address}</Etiqueta>
			<input id={id("Dir")} className="psInput" value={datos.address || ""} onChange={(e) => poner("address", e.target.value)} onBlur={(e) => poner("address", textoPropio(e.target.value))} />
			<Etiqueta htmlFor={id("Cp")}>{f.zipcode}</Etiqueta>
			<input id={id("Cp")} className="psInput" inputMode="numeric" value={datos.zipcode || ""} onChange={(e) => poner("zipcode", e.target.value)} />

			<div className="psDatosSub">Estudios</div>
			<Etiqueta htmlFor={id("Grado")}>{f.academicDegreeId}</Etiqueta>
			<PsSelect id={id("Grado")} value={datos.academicDegreeId || ""} onChange={(e) => poner("academicDegreeId", e.target.value)}>
				<option value="">{S.select}</option>
				{academicDegrees.map((d: any) => <option key={"ad_" + d.id} value={d.id}>{d.name}</option>)}
			</PsSelect>
			<Etiqueta htmlFor={id("Esc")}>{f.lastSchoolName}</Etiqueta>
			<input id={id("Esc")} className="psInput" value={datos.lastSchoolName || ""} onChange={(e) => poner("lastSchoolName", e.target.value)} onBlur={(e) => poner("lastSchoolName", textoPropio(e.target.value))} />
			<Etiqueta htmlFor={id("Ocu")}>{f.currentOccupation}</Etiqueta>
			<input id={id("Ocu")} className="psInput" value={datos.currentOccupation || ""} onChange={(e) => poner("currentOccupation", e.target.value)} onBlur={(e) => poner("currentOccupation", textoPropio(e.target.value))} />
			<Etiqueta htmlFor={id("Obs")}>{f.observations}</Etiqueta>
			<textarea id={id("Obs")} className="psInput" rows={2} value={datos.observations || ""} onChange={(e) => poner("observations", e.target.value)} />

			<div className="psDatosSub">Emergencia</div>
			{contactos.map((c, i) => (
				<div key={"ct_" + i} className="psContacto">
					<div className="psTarjetaCabeza">
						<b style={{ fontSize: 15 }}>Contacto {i + 1}</b>
						{contactos.length > 1 ? <button type="button" className="psQuitarTexto" onClick={() => poner("contacts", contactos.filter((_, j) => j !== i))}>Quitar</button> : null}
					</div>
					<Etiqueta htmlFor={id("CtN" + i)}>{f.contactName}</Etiqueta>
					<input id={id("CtN" + i)} className="psInput" value={c.name} onChange={(e) => ponerContacto(i, "name", e.target.value)} onBlur={(e) => ponerContacto(i, "name", nombrePropioTexto(e.target.value))} />
					<div className="psDos">
						<div>
							<Etiqueta htmlFor={id("CtR" + i)}>{f.contactRelation}</Etiqueta>
							<input id={id("CtR" + i)} className="psInput" value={c.relation} onChange={(e) => ponerContacto(i, "relation", e.target.value)} onBlur={(e) => ponerContacto(i, "relation", primeraMayuscula(e.target.value))} />
						</div>
						<div>
							<Etiqueta htmlFor={id("CtM" + i)}>{f.contactMedia}</Etiqueta>
							<input id={id("CtM" + i)} className="psInput" inputMode="tel" value={c.contactMedia} onChange={(e) => ponerContacto(i, "contactMedia", e.target.value)} />
						</div>
					</div>
				</div>
			))}
			<button type="button" className="psMas chico" onClick={() => poner("contacts", [...contactos, { name: "", relation: "", contactMedia: "" }])}>
				<FontAwesomeIcon icon={solid("plus")} />&nbsp; {S.addContact}
			</button>
		</div>
	);
};

export default DatosCliente;
